import React, { useCallback, useMemo, useState } from "react";
import BagIcon from "components/icons/BagIcon";
import ClockIcon from "components/icons/ClockIcon";
import CalendarIcon from "components/icons/CalendarIcon";
import GraphIcon2 from "components/icons/GraphIcon2";
import { toPng } from "html-to-image";
import TopWidgetInformation from "components/common/TopWidgetInformation";
import LineChart from "components/ReportSales/LineChart";
import BarChart from "components/ReportSales/BarChart";
import ProductsList from "components/common/ListWidget";
import moment from "moment";
import {
  useOrderDates,
  useOrderHourly,
  useOrderMonthly,
} from "graphql/useOrders";
import {
  useMostSoldItem,
  useOrderItemSummary,
} from "graphql/useOrderItemSummary";
import { useOrderSummary } from "graphql/useOrderSummary";
import { useBusinessContext } from "BusinessApp";
import { utils, writeFile } from "xlsx";
import {
  cleanTodaySells,
  filterSortMapSellsByDay,
  filterSortMapSellsByHourSameDay,
  filterSortMapSellsByMonth,
  getDateHeader,
  mapToEntity,
} from "helpers/dataHelper";
import { useTranslation } from "react-i18next";
import TopMenu from "components/Reports/TopMenu";
import useItems from "../../graphql/useItems";
import s from "./styles";
import { useDatesContext } from "../../context/DatesContext";
import { printDocumentPDF } from "helpers/utils";
import DocumentPDF from "./DocumentPDF";

function ReportSales() {
  const { t } = useTranslation();

  const { day } = useDatesContext();

  const [sorting, setSorting] = useState({ columnId: "quantity", order: 1 });

  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const firstGraph = React.useRef(null);
  const secondGraph = React.useRef(null);

  const { storeId, currencyFormat } = useBusinessContext();

  // top information sells and earnings
  const { orderSummary, loading: loading1 } = useOrderSummary({
    storeId,
    start: date.range.start,
    end: date.range.end,
    type: "fac",
  });

  const { orderSummary: orderSummaryCre, loading: loading7 } = useOrderSummary({
    storeId,
    start: date.range.start,
    end: date.range.end,
    type: "cre",
  });

  const { orderItemSummary, loading: loading2 } = useOrderItemSummary({
    storeId,
    start: date.range.start,
    end: date.range.end,
    sort: sorting.columnId,
    order: sorting.order,
    limit: 100,
    type: null,
  });

  const mostSoldItems = useMostSoldItem(orderItemSummary);

  const { items, loading: loading3 } = useItems();

  const itemsMap = useMemo(() => mapToEntity(items), [items]);
  const orderItemSummaryMapped = useMemo(
    () =>
      (orderItemSummary &&
        orderItemSummary.map((item) => [
          itemsMap[item._id.itemId]?.sku || <p>{t("general.deleteItem")}</p>,
          (item._id && item._id.itemName) || "-",
          (item._id && item._id.subcategoryName) || "-",
          `${item._id && currencyFormat(item._id.unitPrice)}`,
          `${currencyFormat(item.total)}`,
          parseInt(item.quantity),
        ])) ||
      [],
    [itemsMap, orderItemSummary]
  );

  const orderMostSoldItemsSummaryMapped = useMemo(() => {
    let sortedItems = [...mostSoldItems];
    const compareItems = (a, b) => {
      const aValue = a[sorting.columnId];
      const bValue = b[sorting.columnId];
      if (aValue > bValue) {
        return -sorting.order;
      }
      if (aValue < bValue) {
        return sorting.order;
      }
      return 0;
    };

    sortedItems.sort(compareItems);

    return sortedItems
      .filter((itemFilter) => itemFilter.key !== null)
      .map((item) => ({
        name: item.name || "-",
        group: item.group || "-",
        price: parseFloat(itemsMap[item.key]?.price || 0),
        total: parseFloat(item.total),
        quantity: parseFloat(item.quantity),
        percent: item.percent,
      }));
  }, [mostSoldItems, sorting, itemsMap]);

  const tableData = useMemo(() => {
    return orderMostSoldItemsSummaryMapped.map((item) => [
      item.name,
      item.group,
      currencyFormat(item.price),
      currencyFormat(item.total),
      item.quantity,
      `${parseFloat(item.percent).toFixed(2)} %`,
    ]);
  }, [orderMostSoldItemsSummaryMapped]);

  const isPeriodDay = date.periodType === "date";
  const isPeriodWeekOrMonth =
    date.periodType === "quarter" ||
    date.periodType === "month" ||
    date.periodType === "range";
  const isPeriodYear = date.periodType === "year";

  let barChartTitle = t("containers.ReportSales.daySells");
  if (isPeriodYear) {
    barChartTitle = t("containers.ReportSales.yearSells");
  } else if (date.periodType === "quarter") {
    barChartTitle = t("containers.ReportSales.quarterSells");
  } else if (date.periodType === "month") {
    barChartTitle = t("containers.ReportSales.monthSells");
  }

  const { orderHourlySummary: todayOrderHourlySummary, loading: loading4 } =
    useOrderHourly(storeId, date.range.start, date.range.end);

  const { orderDateSummary, loading: loading5 } = useOrderDates(
    storeId,
    date.range.start,
    date.range.end
  );

  const daySells = useMemo(
    () => Math.max(...orderDateSummary.map((item) => item.total)),
    [orderDateSummary]
  );

  const dayData = useMemo(
    () => orderDateSummary.filter((item) => item.total * 1 === daySells)[0],
    [orderDateSummary, daySells]
  );

  const dateSells = new Date(
    dayData?._id?.year,
    dayData?._id?.month - 1,
    dayData?._id?.dayOfMonth
  );

  const { orderMonthlySummary, loading: loading6 } = useOrderMonthly(
    storeId,
    isPeriodYear ? date.range.start : day.start,
    isPeriodYear ? date.range.end : day.start
  );

  const hourSellsUp = Math.max(
    ...todayOrderHourlySummary.map((item) => item.total)
  );

  const biggestSalesHour = todayOrderHourlySummary.filter(
    (item) => item.total * 1 === hourSellsUp
  )[0];

  const graphData = useMemo(() => {
    let graphData = [];
    if (isPeriodDay) {
      graphData = cleanTodaySells(
        filterSortMapSellsByHourSameDay(todayOrderHourlySummary)
      );
    } else if (isPeriodWeekOrMonth) {
      graphData = filterSortMapSellsByDay(
        orderDateSummary,
        date.range.start,
        date.range.end
      );
      if (date.periodType === "month") {
        graphData = graphData.map((data, i) => ({
          ...data,
          label: i + 1,
        }));
      }
    } else {
      graphData = filterSortMapSellsByMonth(
        orderMonthlySummary,
        date.range.start,
        date.range.end
      );
    }
    return graphData;
  }, [
    date.periodType,
    date.range.end,
    date.range.start,
    isPeriodDay,
    isPeriodWeekOrMonth,
    orderDateSummary,
    orderMonthlySummary,
    todayOrderHourlySummary,
  ]);

  const loading =
    loading1 ||
    loading2 ||
    loading3 ||
    loading4 ||
    loading5 ||
    loading6 ||
    loading7;

  const summarySalesHeader = useMemo(
    () => ({
      count: orderSummary.count || "0",
      total: currencyFormat(orderSummary.total),
      date:
        moment(dateSells).format("DD [de] MMMM [de] YYYY") === "Fecha inválida"
          ? "-"
          : moment(dateSells).format("DD [de] MMMM [de] YYYY"),
      hour: biggestSalesHour?._id?.hour
        ? `${biggestSalesHour?._id.hour}:00 h`
        : "-",
    }),
    [
      biggestSalesHour?._id.hour,
      dateSells,
      orderSummary.count,
      orderSummary.total,
    ]
  );

  const earnings = useMemo(() => {
    const total = parseFloat(orderSummary.total) || 0;
    const extraAmount1 = parseFloat(orderSummary.extraAmount1) || 0;
    const totalCre = parseFloat(orderSummaryCre.total) || 0;

    return total - extraAmount1 - totalCre;
  }, [orderSummary, orderSummaryCre.total]);

  const totalSalesSum = useMemo(() => {
    return orderMostSoldItemsSummaryMapped.reduce((acc, curr) => {
      return acc + curr.quantity;
    }, 0);
  }, [orderMostSoldItemsSummaryMapped]);

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t("containers.ReportSales.reportSalesFor")} ${getDateHeader(
          date.range.start,
          date.range.end
        )}`,
      ],
      [
        t("containers.ReportSales.sells"),
        t("containers.ReportSales.earnings"),
        t("containers.ReportSales.dayWithSells"),
        t("containers.ReportSales.hourSells"),
      ],
      [
        parseInt(totalSalesSum),
        earnings,
        summarySalesHeader.date,
        summarySalesHeader.hour,
      ],
      [],
      [t("containers.ReportSales.mostSold")],
      [
        t("containers.ReportSales.article"),
        t("containers.ReportSales.subcategory"),
        t("containers.ReportSales.price"),
        t("containers.ReportSales.allSells"),
        t("containers.ReportSales.soldUnits"),
        t("containers.ReportSales.percentage"),
      ],
    ];

    orderMostSoldItemsSummaryMapped.forEach((item) => {
      worksheetData.push([
        item.name,
        item.group,
        item.price,
        item.total,
        item.quantity,
        parseFloat(item.percent).toFixed(2),
      ]);
    });

    const workbook = utils.book_new();
    const sheetName = t("containers.ReportSales.reportSales");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(workbook, `${t("containers.ReportSales.reportSales")}.xlsx`);
  }, [
    date.range.start,
    date.range.end,
    orderSummary,
    orderSummaryCre,
    earnings,
    summarySalesHeader.count,
    summarySalesHeader.total,
    summarySalesHeader.date,
    summarySalesHeader.hour,
    orderMostSoldItemsSummaryMapped,
  ]);

  const getImageGraph = useCallback(async () => {
    const img1 = await toPng(firstGraph.current, { cacheBust: true });

    const img2 = await toPng(secondGraph.current, { cacheBust: true });

    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end)[0],
          summarySalesHeader,
          totalSalesSum,
          earnings: currencyFormat(earnings),
          tableData,
          orderSummary,
          img1,
          img2,
        }}
      />,
      `${t("containers.ReportSales.reportSales")}.pdf`
    );
  }, [
    date.range.end,
    date.range.start,
    orderSummary,
    orderSummaryCre,
    totalSalesSum,
    tableData,
    summarySalesHeader,
    t,
  ]);

  return (
    <s.PageContainer>
      <TopMenu
        loading={loading}
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={orderItemSummaryMapped.length === 0}
      />

      <TopWidgetInformation
        elements={[
          {
            value: `${parseInt(totalSalesSum) || "0"}`,
            text: t("containers.ReportSales.sells"),
            icon: <BagIcon />,
          },
          {
            value: currencyFormat(
              orderSummary.total -
                orderSummary.extraAmount1 -
                orderSummaryCre.total
            ),
            text: t("containers.ReportSales.earnings"),
            icon: <GraphIcon2 />,
          },
          {
            value:
              moment(dateSells).format("DD [de] MMMM [de] YYYY") ===
              "Fecha inválida"
                ? "-"
                : moment(dateSells).format("DD [de] MMMM [de] YYYY"),
            text: t("containers.ReportSales.dayWithSells"),
            icon: <CalendarIcon />,
          },
          {
            value: biggestSalesHour?._id?.hour
              ? `${biggestSalesHour._id.hour}:00 h`
              : "-",
            text: t("containers.ReportSales.hourSells"),
            icon: <ClockIcon />,
          },
        ]}
      />

      <s.ChartsContainer>
        <div
          ref={secondGraph}
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
          }}
        >
          <LineChart
            list={graphData}
            loading={loading}
            firstGraph={firstGraph}
          />
          <BarChart
            title={barChartTitle}
            list={graphData}
            loading={loading}
            secondGraph={secondGraph}
          />
        </div>
      </s.ChartsContainer>

      <ProductsList
        width="1000px"
        items={tableData}
        columns={[
          {
            title: t("containers.ReportSales.article"),
          },
          {
            title: t("containers.ReportSales.subcategory"),
            // t("containers.ReportSales.article")
          },
          { title: t("containers.ReportSales.price") },
          {
            title: t("containers.ReportSales.allSells"),
          },
          {
            title: t("containers.ReportSales.soldUnits"),
            sortable: true,
            columnId: "quantity",
          },
          {
            title: t("containers.ReportSales.percentage"),
            sortable: true,
            columnId: "percent",
          },
        ]}
        sorting={sorting}
        changeSorting={(columnId, order) => setSorting({ columnId, order })}
        title={t("containers.ReportSales.mostSold")}
        tableStyle={{ gridTemplateColumns: "16fr 21fr 19fr 12fr 14fr 18fr" }}
        loading={loading}
      />
    </s.PageContainer>
  );
}

export default ReportSales;
